import { JSX } from 'react'

import { Controller, FieldValues } from 'react-hook-form'

import { Group } from '~/components/component-library/Group'
import { TokenSelectorTriggerOnly } from '~/components/component-library/TokenSelector/TokenSelectorTriggerOnly'
import { TokenSelectorWithAmount } from '~/components/component-library/TokenSelector/TokenSelectorWithAmount'
import {
  BalancesProps,
  TokenSelectorProps,
} from '~/components/component-library/TokenSelector/types'
import { SelectedToken } from '~/components/component-library/TokenSelector/useSelectedAggregatedBalance'

export function TokenSelector<FormType extends FieldValues = FieldValues>(
  props: TokenSelectorProps<FormType>,
): JSX.Element {
  const {
    control,
    addressInputName,
    chainIdInputName,
    allowRelayNetworks,
    tokenFilter,
    strictTokenFilter,
    chainFilter,
    onTokenSelect,
    disabled,
    disabledReason,
    label,
    secondaryLabel,
    tooltip,
    secondaryDisplay,
    description,
    vmTypes,
    promotedTokens,
  } = props

  // Re-pack into BalancesProps so the discriminated union stays intact when we
  // forward to TriggerOnly (which only accepts the union, not loose fields).
  const balancesProps: BalancesProps =
    props.showBalances === true
      ? { showBalances: true, sortBy: props.sortBy }
      : { showBalances: false, sortBy: props.sortBy }

  const content = (
    <Controller
      control={control}
      name={addressInputName}
      render={({ field: addressField }) => (
        <Controller
          control={control}
          name={chainIdInputName}
          render={({ field: chainIdField }) => {
            const addressValue = addressField.value ?? ''
            const chainIdValue = Number(chainIdField.value ?? 0)
            const token: SelectedToken =
              addressValue && Number.isFinite(chainIdValue) && chainIdValue > 0
                ? { address: addressValue, chainId: chainIdValue }
                : null

            const handleTokenSelect = (t: NonNullable<SelectedToken>) => {
              if (addressValue !== t.address) {
                addressField.onChange(t.address)
              }
              if (chainIdValue !== t.chainId) {
                chainIdField.onChange(t.chainId)
              }
              onTokenSelect?.(t)
            }

            if (!props.hideAmountInput) {
              return (
                <Controller
                  control={control}
                  name={props.amountInputName}
                  render={({ field: amountField }) => (
                    <TokenSelectorWithAmount
                      token={token}
                      onTokenChange={handleTokenSelect}
                      amount={amountField.value ?? ''}
                      onAmountChange={amountField.onChange}
                      onAmountFocus={props.onAmountFocus}
                      placeholder={props.placeholder}
                      filterAccountAddress={props.filterAccountAddress}
                      showBalances={balancesProps.showBalances ?? false}
                      denomination={props.denomination ?? 'switch'}
                      allowRelayNetworks={allowRelayNetworks}
                      tokenFilter={tokenFilter}
                      strictTokenFilter={strictTokenFilter}
                      chainFilter={chainFilter}
                      disabled={disabled}
                      disabledReason={disabledReason}
                      isLoading={props.isLoading}
                      onExceedsBalanceChange={props.onExceedsBalanceChange}
                      vmTypes={vmTypes}
                      promotedTokens={promotedTokens}
                      balanceOverride={props.balanceOverride}
                      sortBy={balancesProps.sortBy}
                    />
                  )}
                />
              )
            }

            return (
              <TokenSelectorTriggerOnly
                token={token}
                onTokenChange={(t) =>
                  t ? handleTokenSelect(t) : props.onTokenClear()
                }
                placeholder={props.placeholder}
                filterAccountAddress={props.filterAccountAddress}
                allowRelayNetworks={allowRelayNetworks}
                tokenFilter={tokenFilter}
                strictTokenFilter={strictTokenFilter}
                chainFilter={chainFilter}
                disabled={disabled}
                disabledReason={disabledReason}
                promotedTokens={promotedTokens}
                {...balancesProps}
              />
            )
          }}
        />
      )}
    />
  )

  if (label || secondaryLabel || tooltip || secondaryDisplay || description) {
    return (
      <Group
        label={label}
        secondaryLabel={secondaryLabel}
        tooltip={tooltip}
        secondaryDisplay={secondaryDisplay}
        description={description}
      >
        {content}
      </Group>
    )
  }

  return content
}
